// The Tools tab: what the agent can do on its own — each tool, what it does (its own description, the text the
// agent reads), where it comes from, in two groups: the tools that read data and those that can change it. A project
// agent's manager adds a tool by saying in plain words what the agent should be able to do: the model finds the
// operation and the page adds it at once (one commit, with Undo); one that changes data is asked first; when none
// fits, the builder can make it. A row's menu edits its description or
// removes it. A platform agent's tools are read-only. When the model finds nothing (or cannot be asked), the list of
// operations is offered to pick one by hand.
import type { Agent } from '@cortex/sdk-agents-client';
import { useConfirm } from '@cortex/ui';
import { Check, Loader2, MoreVertical, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import * as React from 'react';
import { useAgentsHost, useAgentsKey, useAgentsQuery, useInvalidate } from './host.tsx';
import { toolText, type ToolCatalog } from './model.ts';
import type { AgentTabContext } from './tabs.tsx';
import { ToolsPicker, type DraftTool } from './ToolsPicker.tsx';
import { btnGhost, btnPrimary, iconBtn, input, messageOf, Modal, unwrap } from './ui.tsx';

/** A tool as the agent's PUT takes it. */
interface ToolWrite {
  name: string;
  source: 'operation' | 'app' | 'sdk';
  operationId: string;
  app?: string | null;
  description: string | null;
}

const writeOf = (a: Agent): ToolWrite[] =>
  a.tools.map((t) => ({
    name: t.name,
    source: t.source,
    operationId: t.operationId,
    ...(t.source === 'app' || t.source === 'sdk' ? { app: t.app } : {}),
    description: t.description ?? null,
  }));

interface Row {
  key: string;
  name: string;
  what: string;
  inputs: string[];
  from: string;
  writes: boolean;
  problem: string | null;
}

/**
 * The permission of each tool, as the harness (Tools' Permissions) gives it to the tools table: one row per tool, its
 * permission and its calls beside what it does; the rule for every other tool under the table.
 */
export interface PermissionCells {
  cell(tool: string): React.ReactNode;
  /** The calls column's head ("Calls, last 12 runs"), or null when the runs are not known. */
  callsHead: string | null;
  calls(tool: string): React.ReactNode;
  footer: React.ReactNode;
}

const Empty = ({ children }: { children: React.ReactNode }) => (
  <div className="text-sm text-page-muted" data-testid="agent-tab-tools-empty">
    {children}
  </div>
);

/** A long description is cut to two lines; a click shows it all. */
function What({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className={'cursor-pointer ' + (open ? '' : 'line-clamp-2')}
      title={open ? undefined : 'Show all'}
      onClick={() => setOpen(!open)}
    >
      {text}
    </div>
  );
}

function rowsOf(a: Agent): Row[] {
  return a.tools.map((t) => ({
    key: `${t.app ?? ''}/${t.name}`,
    name: t.name,
    ...toolText(t.description || t.operation?.summary || '', t.operation?.inputSchema),
    from:
      t.source === 'app'
        ? `app ${t.app}`
        : t.source === 'sdk'
          ? `SDK ${t.app}`
          : `platform${t.operation?.tag ? ` · ${t.operation.tag}` : ''}`,
    writes: t.operation?.access === 'write',
    problem: t.problem,
  }));
}

function Table({
  rows,
  grouped,
  actions,
  permissions,
}: {
  rows: Row[];
  grouped: boolean;
  /** A row's menu (a project agent's manager): edit its description, remove it. */
  actions?: (r: Row) => React.ReactNode;
  permissions?: PermissionCells | null;
}) {
  // No tool says what it does (a platform agent's built-in tools have no descriptions yet): no empty column.
  const described = rows.some((r) => r.what.trim() || r.inputs.length || r.problem);
  const cols =
    1 + (described ? 1 : 0) + (permissions ? 1 : 0) + (permissions?.callsHead ? 1 : 0) + (actions ? 1 : 0);
  const groups = grouped
    ? [
        { id: 'reads', label: 'Reads data', rows: rows.filter((r) => !r.writes) },
        { id: 'writes', label: 'Can change data', rows: rows.filter((r) => r.writes) },
      ].filter((g) => g.rows.length)
    : [{ id: 'all', label: '', rows }];
  return (
    <div className="overflow-x-auto rounded-md border border-card-border bg-card-bg">
      <table className="w-full text-sm border-collapse" data-testid="agent-tab-tools-list">
        <thead>
          <tr className="text-left text-xs text-page-muted">
            <th className="py-2 px-3 font-medium">Tool</th>
            {described ? <th className="py-2 px-3 font-medium">What it does</th> : null}
            {permissions ? <th className="py-2 px-3 font-medium">Permission</th> : null}
            {permissions?.callsHead ? (
              <th className="py-2 px-3 font-medium whitespace-nowrap hidden sm:table-cell">
                {permissions.callsHead}
              </th>
            ) : null}
            {actions ? <th className="w-8" /> : null}
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.id} data-testid={`agent-tab-tools-${g.id}`}>
            {g.label ? (
              <tr>
                <td
                  colSpan={cols}
                  className="border-t border-divider bg-page-bg px-3 py-1 text-xs font-semibold text-page-muted"
                >
                  {g.label}
                </td>
              </tr>
            ) : null}
            {g.rows.map((r) => (
              <tr
                key={r.key}
                className="border-t border-divider align-top"
                data-testid={`agent-tab-tool-${r.name}`}
              >
                <td className="py-2 px-3 whitespace-nowrap">
                  <div className="font-mono text-[13px] text-page-title">{r.name}</div>
                  <div className="text-[11px] text-page-muted" data-testid={`agent-tab-tool-from-${r.name}`}>
                    {r.from}
                  </div>
                </td>
                {described ? (
                  <td className="py-2 px-3">
                    <What text={r.what} />
                    {r.inputs.length ? (
                      <div
                        className="mt-0.5 text-[11px] text-page-muted"
                        data-testid={`agent-tab-tool-inputs-${r.name}`}
                      >
                        Inputs: {r.inputs.join(', ')}
                      </div>
                    ) : null}
                    {r.problem ? (
                      <div className="mt-1 text-xs text-amber-700 dark:text-amber-400">{r.problem}</div>
                    ) : null}
                  </td>
                ) : null}
                {permissions ? <td className="py-2 px-3">{permissions.cell(r.name)}</td> : null}
                {permissions?.callsHead ? (
                  <td className="py-2 px-3 text-xs text-page-muted whitespace-nowrap hidden sm:table-cell">
                    {permissions.calls(r.name)}
                  </td>
                ) : null}
                {actions ? <td className="py-1 pr-2 text-right">{actions(r)}</td> : null}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
      {permissions ? (
        <div className="border-t border-divider px-3 py-2 text-xs text-page-muted">{permissions.footer}</div>
      ) : null}
    </div>
  );
}

/** A row's menu: edit the description the agent reads, or remove the tool. */
function RowMenu({ onEdit, onRemove, name }: { onEdit(): void; onRemove(): void; name: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        className={iconBtn}
        title="More"
        data-testid={`agent-tool-menu-${name}`}
        onClick={() => setOpen(!open)}
      >
        <MoreVertical size={14} />
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-20 mt-1 min-w-44 rounded-md border border-card-border bg-card-bg p-1 text-left shadow-lg">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm border-0 bg-transparent cursor-pointer hover:bg-page-bg"
            data-testid={`agent-tool-edit-${name}`}
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            <Pencil size={13} /> Edit description
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm text-red-600 border-0 bg-transparent cursor-pointer hover:bg-page-bg"
            data-testid={`agent-tool-remove-${name}`}
            onClick={() => {
              setOpen(false);
              onRemove();
            }}
          >
            <Trash2 size={13} /> Remove
          </button>
        </div>
      ) : null}
    </div>
  );
}

type Found = Awaited<ReturnType<ReturnType<typeof useAgentsHost>['client']['suggestTools']>>['tools'][number];

/**
 * Add a tool: what the agent should be able to do, in plain words; the model finds the operation and it is added at
 * once (Undo takes it back); one that changes data is asked first; none fits — the builder can make it. The list of
 * operations is one click away.
 */
function AddTool({
  agent,
  save,
  onBuilder,
  onClose,
}: {
  agent: Agent;
  save(tools: ToolWrite[]): Promise<void>;
  onBuilder: ((need: string) => void) | null;
  onClose(): void;
}) {
  const host = useAgentsHost();
  const [need, setNeed] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [found, setFound] = React.useState<{ tools: Found[]; none: string | null } | null>(null);
  const [ask, setAsk] = React.useState<Found | null>(null);
  const [added, setAdded] = React.useState<Found[]>([]);
  const [listing, setListing] = React.useState(false);
  // The model found nothing, or could not be asked (no model on this host, an error): the list is offered from then on.
  const [stuck, setStuck] = React.useState(false);
  const [catalog, setCatalog] = React.useState<ToolCatalog | null>(null);
  const base = React.useRef(writeOf(agent));

  const add = async (t: Found) => {
    setBusy(true);
    setErr('');
    try {
      const next = [
        ...base.current,
        {
          name: t.name,
          source: t.source,
          operationId: t.operationId,
          ...(t.source === 'app' || t.source === 'sdk' ? { app: t.app } : {}),
          description: t.description,
        },
      ];
      await save(next);
      base.current = next;
      setAdded((a) => [...a, t]);
      setAsk(null);
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const undo = async (t: Found) => {
    setBusy(true);
    try {
      const next = base.current.filter((x) => x.name !== t.name);
      await save(next);
      base.current = next;
      setAdded((a) => a.filter((x) => x.name !== t.name));
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const find = async () => {
    if (!need.trim()) return;
    setBusy(true);
    setErr('');
    setFound(null);
    setAsk(null);
    try {
      const r = await host.client.suggestTools(agent.key, need.trim());
      setFound(r);
      if (!r.tools.length) setStuck(true);
      setBusy(false);
      const best = r.tools[0];
      if (best) {
        if (best.access === 'write') setAsk(best);
        else await add(best);
      }
    } catch (e) {
      setErr(messageOf(e));
      setStuck(true);
      setBusy(false);
    }
  };
  React.useEffect(() => {
    if (!listing || catalog) return;
    let alive = true;
    unwrap(host.client.fetch.GET('/agent-tools'))
      .then((c) => alive && setCatalog(c))
      .catch((e) => alive && setErr(messageOf(e)));
    return () => {
      alive = false;
    };
  }, [listing, catalog]);
  const others = (found?.tools ?? []).slice(1).filter((t) => !added.some((a) => a.name === t.name));

  return (
    <Modal
      title={
        <>
          <Plus size={16} /> Add a tool to “{agent.title}”
        </>
      }
      onClose={onClose}
      testId="agent-add-tool"
      wide
      footer={
        <>
          <button type="button" className={btnGhost} data-testid="agent-add-tool-close" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className={btnPrimary}
            data-testid="agent-add-tool-go"
            disabled={busy || !need.trim()}
            onClick={() => void find()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Add
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <label className="block">
          <span className="text-xs text-page-muted">What should it be able to do?</span>
          <textarea
            className={input + ' min-h-[72px]'}
            data-testid="agent-add-tool-need"
            value={need}
            maxLength={2000}
            placeholder="e.g. Read a case study with its metrics, so the screens show the real numbers."
            onChange={(e) => setNeed(e.target.value)}
          />
        </label>
        <div className="text-xs text-page-muted">
          AI finds the operation of this project’s apps or the platform that does it, adds it and writes for
          the agent what it does.
        </div>
        {added.map((t) => (
          <div
            key={t.name}
            className="flex flex-wrap items-center gap-2 rounded-md border border-card-border px-3 py-2"
            data-testid={`agent-add-tool-added-${t.name}`}
          >
            <Check size={14} className="text-green-600" /> Added <b className="font-mono">{t.name}</b>
            <span className="text-xs text-page-muted">
              {t.access === 'write' ? 'changes data' : 'reads'} · {t.why || t.description}
            </span>
            <button
              type="button"
              className="ml-auto border-0 bg-transparent p-0 text-xs text-nav-active cursor-pointer"
              data-testid={`agent-add-tool-undo-${t.name}`}
              disabled={busy}
              onClick={() => void undo(t)}
            >
              Undo
            </button>
          </div>
        ))}
        {ask ? (
          <div className="rounded-md border border-amber-400 px-3 py-2" data-testid="agent-add-tool-confirm">
            <b className="text-amber-700 dark:text-amber-400">● This one changes data:</b>{' '}
            <b className="font-mono">{ask.name}</b> — {ask.description} The agent will call it without asking
            you.
            <div className="mt-2 flex gap-2">
              <button type="button" className={btnPrimary} disabled={busy} onClick={() => void add(ask)}>
                Add it
              </button>
              <button type="button" className={btnGhost} onClick={() => setAsk(null)}>
                Cancel
              </button>
            </div>
          </div>
        ) : null}
        {others.length ? (
          <div className="text-xs text-page-muted" data-testid="agent-add-tool-also">
            Also found:{' '}
            {others.map((t) => (
              <button
                key={t.name}
                type="button"
                className="mr-2 border-0 bg-transparent p-0 text-xs text-nav-active cursor-pointer"
                title={t.description}
                onClick={() => (t.access === 'write' ? setAsk(t) : void add(t))}
              >
                ＋ {t.name}
              </button>
            ))}
          </div>
        ) : null}
        {found && !found.tools.length ? (
          <div className="rounded-md border border-card-border px-3 py-2" data-testid="agent-add-tool-none">
            {found.none}
            {onBuilder ? (
              <button
                type="button"
                className="ml-2 border-0 bg-transparent p-0 text-nav-active cursor-pointer"
                onClick={() => onBuilder(need)}
              >
                ✨ Ask the builder to make it ›
              </button>
            ) : null}
          </div>
        ) : null}
        {err ? (
          <div className="text-sm text-red-600" data-testid="agent-add-tool-error">
            {err}
          </div>
        ) : null}
        {/* The list of operations only when the model found nothing or could not be asked: pick it yourself. */}
        {stuck ? (
          <div className="border-t border-divider pt-2">
            <button
              type="button"
              className="border-0 bg-transparent p-0 text-xs text-nav-active cursor-pointer"
              data-testid="agent-add-tool-list"
              onClick={() => setListing(!listing)}
            >
              {listing ? 'Hide the list' : 'or pick it yourself from the list of operations'}
            </button>
            {listing ? (
              <ToolsPicker
                catalog={catalog}
                tools={base.current.map((t): DraftTool => ({
                  uid: t.name,
                  name: t.name,
                  source: t.source,
                  operationId: t.operationId,
                  app: t.app ?? null,
                  description: t.description ?? '',
                  operation: null,
                  problem: null,
                  isNew: false,
                  removed: false,
                }))}
                onAdd={(d) =>
                  void add({
                    name: d.name,
                    source: d.source,
                    operationId: d.operationId,
                    app: d.app,
                    access: d.operation?.access ?? 'read',
                    description: d.description || d.operation?.summary || '',
                    why: '',
                  })
                }
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

export function ToolsTab({ agent, permissions }: AgentTabContext & { permissions?: PermissionCells | null }) {
  const host = useAgentsHost();
  const invalidate = useInvalidate();
  const k = useAgentsKey();
  const { confirm } = useConfirm();
  const [changing, setChanging] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [editing, setEditing] = React.useState<{ name: string; text: string } | null>(null);
  const [err, setErr] = React.useState('');
  const projectAgent = agent.kind === 'project';
  const full = useAgentsQuery(k('agent', agent.key), () => host.client.get(agent.key), {
    enabled: projectAgent,
  });
  const save = async (tools: ToolWrite[]) => {
    await host.client.save(agent.key, { tools } as never);
    invalidate([k()]);
  };

  const rows: Row[] = projectAgent
    ? full.data
      ? rowsOf(full.data)
      : []
    : agent.tools.map((t) => ({
        key: `${t.group ?? t.app ?? ''}/${t.name}`,
        name: t.name,
        ...toolText(t.description ?? ''),
        from:
          t.source === 'builtin'
            ? `built-in · ${t.group}`
            : t.source === 'app'
              ? `app ${t.app}`
              : t.source === 'sdk'
                ? `SDK ${t.app}`
                : 'platform',
        writes: t.access === 'write',
        problem: null,
      }));
  const loading = projectAgent && !full.data && !full.error;
  const writes = rows.filter((r) => r.writes).length;
  // Which tools change data is known for a project agent, and for a platform agent whose host says it.
  const accessKnown = projectAgent || agent.tools.some((t) => t.access);
  const canManage = projectAgent && (full.data?.canManage ?? false);

  const remove = async (name: string) => {
    if (!full.data) return;
    const ok = await confirm({
      title: `Remove the tool “${name}”?`,
      description: 'The agent can no longer call it. Saved with the agent, one commit.',
      confirmText: 'Remove',
    });
    if (!ok) return;
    setErr('');
    try {
      await save(writeOf(full.data).filter((t) => t.name !== name));
    } catch (e) {
      setErr(messageOf(e));
    }
  };
  const saveDescription = async () => {
    if (!full.data || !editing) return;
    setErr('');
    try {
      await save(
        writeOf(full.data).map((t) =>
          t.name === editing.name ? { ...t, description: editing.text.trim() || null } : t,
        ),
      );
      setEditing(null);
    } catch (e) {
      setErr(messageOf(e));
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="agent-tab-tools">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm" data-testid="agent-tab-tools-count">
          <b>
            {rows.length} {rows.length === 1 ? 'tool' : 'tools'}
          </b>
          {accessKnown && rows.length ? (
            <span className="text-page-muted">
              {' '}
              · {rows.length - writes} read data · {writes} can change data
            </span>
          ) : null}
        </span>
        {canManage ? (
          <span className="ml-auto flex items-center gap-2" data-testid="agent-tab-tools-change">
            {host.ChangeRequest ? (
              <span className="text-xs text-page-muted">
                or ask{' '}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-xs text-nav-active cursor-pointer hover:underline"
                  data-testid="agent-tab-tools-build"
                  onClick={() => setChanging('')}
                >
                  <Sparkles size={12} /> the builder
                </button>
              </span>
            ) : null}
            <button
              type="button"
              className={btnPrimary}
              data-testid="agent-tab-tools-add"
              onClick={() => setAdding(true)}
            >
              <Plus size={14} /> Add tool
            </button>
          </span>
        ) : null}
      </div>
      {editing ? (
        <div
          className="flex flex-col gap-2 rounded-md border border-card-border p-3"
          data-testid="agent-tool-desc-editor"
        >
          <span className="text-xs text-page-muted">
            What <b className="font-mono">{editing.name}</b> does — the agent reads this to decide when to
            call it
          </span>
          <textarea
            className={input + ' min-h-[64px]'}
            data-testid={`agent-tool-desc-${editing.name}`}
            value={editing.text}
            onChange={(e) => setEditing({ ...editing, text: e.target.value })}
          />
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} onClick={() => void saveDescription()}>
              Save
            </button>
            <button type="button" className={btnGhost} onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {err ? <div className="text-sm text-red-600">{err}</div> : null}
      {full.error ? (
        <div className="text-sm text-red-600">{String(full.error)}</div>
      ) : loading ? (
        <Empty>Loading the tools…</Empty>
      ) : rows.length ? (
        <Table
          rows={rows}
          grouped={accessKnown}
          permissions={permissions ?? null}
          {...(canManage
            ? {
                actions: (r: Row) => (
                  <RowMenu
                    name={r.name}
                    onEdit={() =>
                      setEditing({
                        name: r.name,
                        text: full.data?.tools.find((t) => t.name === r.name)?.description ?? r.what,
                      })
                    }
                    onRemove={() => void remove(r.name)}
                  />
                ),
              }
            : {})}
        />
      ) : (
        <Empty>No tools{projectAgent ? ' — it answers from its instructions alone.' : '.'}</Empty>
      )}
      {adding && full.data ? (
        <AddTool
          agent={full.data}
          save={save}
          onBuilder={
            host.ChangeRequest
              ? (need) => {
                  setAdding(false);
                  setChanging(need);
                }
              : null
          }
          onClose={() => setAdding(false)}
        />
      ) : null}
      {changing !== null && host.ChangeRequest ? (
        <host.ChangeRequest agent={agent.key} title={agent.title} onClose={() => setChanging(null)} />
      ) : null}
    </div>
  );
}
